/**
 * A poll in the thread, and the sheet for starting one.
 *
 * Its own file rather than more of Chat.tsx, which is already the longest
 * screen in the app. The arithmetic and the visibility rule are in
 * shared/polls.ts; what is here is only the drawing of them.
 */
import { useEffect, useMemo, useState } from 'react';
import type { MediaRef, MessageDoc, PollOption, PollVoteDoc, UserDoc } from '../../../shared/types';
import {
  MAX_POLL_OPTIONS,
  canSeeVotes,
  nextChoice,
  pollProblem,
  tallyPoll,
  votersOf,
} from '../../../shared/polls';
import {
  avatarOf,
  closePoll,
  createPoll,
  editPoll,
  urlFor,
  votePoll,
  watchPollVotes,
  type WithId,
} from '../data';
import { enqueueUpload } from '../uploadQueue';
import { scrubVotes, useHidden } from '../hidden';
import {
  Avatar,
  Confirm,
  IconCheck,
  IconClose,
  IconImage,
  IconPlus,
  MediaViewer,
  Sheet,
  Spinner,
} from '../ui';
import { Photo } from '../Photo';
import { shownMedia } from '../../../shared/constants';

/**
 * One poll, as it appears in chat.
 *
 * Two states, and the difference between them is whether you have answered.
 * Before: the question, the options, and how many people have voted — not how
 * they split, because a running total is how a poll talks people into the
 * leading answer. After: the whole thing, with faces.
 */
export function PollRow({
  message,
  uid,
  users,
}: {
  message: WithId<MessageDoc>;
  uid: string;
  users: Record<string, UserDoc>;
}) {
  const poll = message.data.poll;
  const authorUid = message.data.authorUid;
  const mine = uid === authorUid;

  const [allVotes, setVotes] = useState<Record<string, PollVoteDoc>>({});
  // The test account's vote is not counted where the family can see it.
  const hidden = useHidden();
  const votes = useMemo(() => scrubVotes(allVotes, hidden), [allVotes, hidden]);
  // A refusal is the ordinary state of a poll you have not answered, not a
  // fault: the rules will not hand over the votes until you hold one.
  const [allowed, setAllowed] = useState(false);
  /** This person's answer as of the last tap, until the server confirms it. */
  const [want, setWant] = useState<string[] | null>(null);
  /** Which option's picture is open full screen, if any. */
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [asking, setAsking] = useState(false);

  useEffect(
    () => watchPollVotes(message.id, (next, ok) => {
      setVotes(next);
      setAllowed(ok);
    }),
    [message.id],
  );

  if (!poll) return null;

  /*
   * What this tap means, before the server has been asked.
   *
   * A vote is a callable and a listener round trip, which is seconds on a
   * phone — long enough that tapping an answer did nothing at all for a beat
   * and people tapped again. So the choice is made here and the server
   * catches up, exactly as keeping a photo does.
   */
  const stored = votes[uid]?.optionIds ?? [];
  const chosen = want ?? stored;
  const hasVoted = chosen.length > 0;
  const closed = Boolean(poll.closedAt);

  // Once the vote comes back saying what this already says, the local answer
  // has nothing left to add.
  useEffect(() => {
    setWant((wanted) => (wanted && same(wanted, stored) ? null : wanted));
  }, [stored.join(',')]);

  /*
   * Counted with this person's own answer swapped in, so the bars and the
   * faces move on the tap rather than a second later. Everybody else's votes
   * are whatever the listener last said.
   */
  const shown: Record<string, { optionIds: string[] }> = want
    ? { ...votes, [uid]: { optionIds: want } }
    : votes;
  const withoutEmpty = Object.fromEntries(
    Object.entries(shown).filter(([, vote]) => vote.optionIds.length > 0),
  );

  const total = allowed ? votersOf(withoutEmpty) : poll.voteCount + (want && !stored.length ? 1 : 0);
  const open = canSeeVotes(poll, authorUid, uid, hasVoted) && allowed;
  const results = tallyPoll(poll, withoutEmpty);

  const onPick = (optionId: string) => {
    if (closed) return;
    const next = nextChoice(chosen, optionId, poll.multi);
    setWant(next);
    void votePoll({ messageId: message.id, optionIds: next }).catch(() => {
      // Back to whatever the server still believes; the next tap tries again.
      setWant(null);
    });
  };

  return (
    <div className="poll">
      <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
        <span className="grow bold" style={{ fontSize: 15, lineHeight: 1.3 }}>
          {poll.question}
        </span>
        {mine && !closed && (
          <button className="poll-edit" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>

      <span className="tiny dim">
        {poll.multi ? 'Pick as many as you like' : 'One each'}
        {closed ? ' · closed' : ''}
        {poll.editedAt ? ' · edited' : ''}
      </span>

      <div className="col" style={{ gap: 8 }}>
        {results.map(({ option, voters }, at) => {
          const picked = chosen.includes(option.id);
          const share = total > 0 ? Math.round((voters.length / total) * 100) : 0;

          return (
            <div
              key={option.id}
              className={picked ? 'poll-option is-mine' : 'poll-option'}
            >
              {/* Only once the result is legitimately visible, or the bar
                  would leak the split to somebody who has not voted. */}
              {open && <span className="poll-bar" style={{ width: `${share}%` }} />}

              {/*
                * The picture opens rather than voting. It is the one thing on
                * the row that cannot be read at 44 pixels, and a poll about
                * which door is a poll nobody can answer without looking.
                */}
              {option.image ? (
                <button
                  className="poll-face"
                  aria-label={`Look at ${option.label}`}
                  onClick={() => setViewing(at)}
                >
                  <Photo src={urlFor(shownMedia(option.image))} preview={option.image.blur} alt="" loading="lazy" />
                </button>
              ) : (
                <span className="poll-face">
                  <span className="poll-tick">{picked ? <IconCheck size={16} /> : null}</span>
                </span>
              )}

              {/* Everything but the picture votes, and the label is free to
                  wrap inside it rather than being cut off at one line. */}
              <button
                className="poll-choose"
                disabled={closed}
                aria-pressed={picked}
                onClick={() => onPick(option.id)}
              >
                <span className="poll-label">{option.label}</span>
                {open && voters.length > 0 && (
                  <span className="poll-voters">
                    {voters.slice(0, 4).map((voter) => (
                      <Avatar
                        key={voter}
                        name={users[voter]?.displayName ?? '?'}
                        photoURL={avatarOf(users[voter])}
                        size={18}
                      />
                    ))}
                    <span className="tiny dim poll-names">
                      {voters
                        .map((voter) =>
                          voter === uid ? 'You' : (users[voter]?.displayName ?? 'Someone'),
                        )
                        .join(', ')}
                    </span>
                  </span>
                )}
              </button>

              {open && <span className="poll-count">{voters.length}</span>}
            </div>
          );
        })}
      </div>

      <div className="row" style={{ gap: 8 }}>
        <span className="tiny dim grow">
          {total === 0
            ? 'Nobody has voted yet'
            : `${total} ${total === 1 ? 'person has' : 'people have'} voted`}
          {!open && total > 0 ? ' · vote to see who' : ''}
        </span>
        {mine && !closed && (
          <button className="poll-edit" onClick={() => setAsking(true)}>
            Close
          </button>
        )}
      </div>

      {editing && (
        <PollSheet
          poll={{ question: poll.question, options: poll.options, multi: poll.multi }}
          messageId={message.id}
          onClose={() => setEditing(false)}
        />
      )}

      {asking && (
        <Confirm
          title="Close this poll?"
          body="No more votes, and the answers become visible to everyone. It cannot be reopened."
          confirmLabel="Close it"
          onConfirm={() => {
            setAsking(false);
            void closePoll({ messageId: message.id }).catch(() => undefined);
          }}
          onCancel={() => setAsking(false)}
        />
      )}

      {/*
        * The pictures full screen, and paged between, because comparing them
        * is the whole of what a poll with pictures is for.
        */}
      {viewing !== null && (
        <MediaViewer
          items={poll.options
            .filter((option) => option.image)
            .map((option) => ({
              key: option.id,
              src: urlFor(option.image as MediaRef),
              preview: option.image?.small ? urlFor(shownMedia(option.image)) : option.image?.blur,
              who: option.label,
            }))}
          index={Math.max(
            0,
            poll.options.filter((option) => option.image).findIndex(
              (option) => option.id === poll.options[viewing]?.id,
            ),
          )}
          onIndex={(next) => {
            const shown = poll.options.filter((option) => option.image);
            const back = poll.options.findIndex((option) => option.id === shown[next]?.id);
            if (back >= 0) setViewing(back);
          }}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

/** Two answers that name the same options, in whatever order. */
function same(a: string[], b: string[]): boolean {
  return a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');
}

interface Draft {
  id?: string;
  label: string;
  /** Chosen here, uploaded once the poll exists and the option has an id. */
  file?: File;
  preview?: string;
}

/**
 * Starting one, or editing one already asked.
 *
 * The pictures cannot be uploaded until the poll exists, because an upload has
 * to name the option it belongs to and an option has no id until the server
 * has made it. So: create, then attach, which is also why an option keeps its
 * id through an edit.
 */
export function PollSheet({
  poll,
  messageId,
  onClose,
}: {
  poll?: { question: string; options: PollOption[]; multi: boolean };
  messageId?: string;
  onClose: () => void;
}) {
  const [question, setQuestion] = useState(poll?.question ?? '');
  const [multi, setMulti] = useState(poll?.multi ?? false);
  const [options, setOptions] = useState<Draft[]>(
    poll?.options.map((each) => ({ id: each.id, label: each.label })) ?? [
      { label: '' },
      { label: '' },
    ],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problem = pollProblem(question, options.map((each) => each.label));

  const setLabel = (at: number, label: string) =>
    setOptions((current) => current.map((each, i) => (i === at ? { ...each, label } : each)));

  const setFile = (at: number, file?: File) =>
    setOptions((current) =>
      current.map((each, i) =>
        i === at
          ? { ...each, file, preview: file ? URL.createObjectURL(file) : undefined }
          : each,
      ),
    );

  const submit = async () => {
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);

    try {
      const labels = options.map((each) => each.label.trim()).filter(Boolean);

      if (messageId) {
        await editPoll({
          messageId,
          question: question.trim(),
          options: options
            .filter((each) => each.label.trim())
            .map((each) => ({ id: each.id, label: each.label.trim() })),
        });
        onClose();
        return;
      }

      const made = await createPoll({ question: question.trim(), labels, multi });

      /*
       * The pictures follow the poll rather than going with it. Matched back
       * to their options by position among the filled-in ones, which is the
       * same order the server built the ids in.
       */
      const filled = options.filter((each) => each.label.trim());
      filled.forEach((each, i) => {
        const option = made.data.options[i];
        if (!each.file || !option) return;
        enqueueUpload({
          kind: 'chat-photo',
          file: each.file,
          target: { kind: 'poll', id: made.data.messageId, optionId: option.id },
        });
      });

      onClose();
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'That would not send.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={messageId ? 'Edit poll' : 'New poll'} onClose={onClose}>

      <input
        className="reply-input"
        value={question}
        placeholder="What are you asking?"
        autoFocus
        onChange={(event) => setQuestion(event.target.value)}
      />

      <div className="col" style={{ gap: 8 }}>
        {options.map((option, at) => (
          <div key={option.id ?? at} className="row" style={{ gap: 8 }}>
            <label className="poll-pick">
              {option.preview ? (
                <img src={option.preview} alt="" />
              ) : (
                <IconImage size={18} />
              )}
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) => setFile(at, event.target.files?.[0])}
              />
            </label>

            <input
              className="reply-input grow"
              value={option.label}
              placeholder={`Option ${at + 1}`}
              onChange={(event) => setLabel(at, event.target.value)}
            />

            {options.length > 2 && (
              <button
                className="icon-btn"
                aria-label={`Remove option ${at + 1}`}
                onClick={() => setOptions((current) => current.filter((_, i) => i !== at))}
              >
                <IconClose size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      {options.length < MAX_POLL_OPTIONS && (
        <button
          className="btn btn--ghost"
          onClick={() => setOptions((current) => [...current, { label: '' }])}
        >
          <IconPlus size={16} />
          <span style={{ marginLeft: 6 }}>Add an option</span>
        </button>
      )}

      {/* Fixed once it is asked: changing it under people who have already
          answered would silently reinterpret their votes. */}
      {!messageId && (
        <label className="row" style={{ gap: 10, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={multi}
            onChange={(event) => setMulti(event.target.checked)}
          />
          <span className="small">Let people pick more than one</span>
        </label>
      )}

      {(error || problem) && (
        <span className="tiny" style={{ color: 'var(--danger)' }}>
          {error ?? problem}
        </span>
      )}

      <button className="btn btn--block" disabled={busy || Boolean(problem)} onClick={() => void submit()}>
        {busy ? <Spinner /> : messageId ? 'Save' : `Ask ${options.filter((o) => o.label.trim()).length} ways`}
      </button>
    </Sheet>
  );
}
